import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollText, Search, Filter, Download, User, Shield, Settings, LogIn, FileText, Trash2, RefreshCw, Building2, Key, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const AuditLogsPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/audit-logs');
      const data = response.data;
      
      // Transform the data to match expected format
      const formattedLogs = (data.items || []).map(log => ({
        id: log.id,
        timestamp: log.timestamp,
        user: log.user_email || log.user_id,
        action: log.action,
        resource: log.resource_type,
        details: formatDetails(log),
        ip: log.ip_address || 'N/A',
        status: log.success ? 'success' : 'failed',
        tenant_id: log.tenant_id
      }));
      
      setLogs(formattedLogs);
      setTotal(data.total || formattedLogs.length);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      toast.error('Failed to load audit logs');
      // Fall back to sample data
      setLogs(sampleLogs);
    } finally {
      setLoading(false);
    }
  };

  const formatDetails = (log) => {
    if (log.details) {
      if (typeof log.details === 'string') return log.details;
      // Format object details into readable string
      const details = log.details;
      if (details.name) return `Name: ${details.name}`;
      if (details.target_user) return `User: ${details.target_user}`;
      if (details.visible_frameworks) return `Frameworks: ${details.visible_frameworks.join(', ')}`;
      if (details.modules) return `Modules: ${details.modules.join(', ')}`;
      return JSON.stringify(details).substring(0, 100);
    }
    return log.action;
  };

  const sampleLogs = [
    { id: '1', timestamp: new Date().toISOString(), user: 'admin@beskar.io', action: 'login', resource: 'auth', details: 'Successful login', ip: '192.168.1.1', status: 'success' },
  ];

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4" />,
      create: <FileText className="w-4 h-4" />,
      update: <Settings className="w-4 h-4" />,
      delete: <Trash2 className="w-4 h-4" />,
      scan: <Shield className="w-4 h-4" />,
      export: <Download className="w-4 h-4" />,
      'tenant:delete': <Trash2 className="w-4 h-4" />,
      'tenant:update_visible_frameworks': <Settings className="w-4 h-4" />,
      'user:add_preview': <User className="w-4 h-4" />,
      'tenant:create': <Building2 className="w-4 h-4" />,
      'tenant:update': <Settings className="w-4 h-4" />,
      'user:create': <User className="w-4 h-4" />,
      'role:update': <Key className="w-4 h-4" />,
    };
    return icons[action] || <FileText className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'bg-blue-100 text-blue-700',
      create: 'bg-green-100 text-green-700',
      update: 'bg-amber-100 text-amber-700',
      delete: 'bg-red-100 text-red-700',
      scan: 'bg-purple-100 text-purple-700',
      export: 'bg-indigo-100 text-indigo-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    
    return matchesSearch && matchesAction;
  });

  const uniqueActions = [...new Set(logs.map(l => l.action))];

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-gray-500" />
            Audit Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all system activities and user actions
          </p>
        </div>
        <Button variant="outline" data-testid="export-logs-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Logs
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-logs-input"
          />
        </div>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-40" data-testid="filter-action">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {uniqueActions.map(action => (
              <SelectItem key={action} value={action} className="capitalize">{action}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline">{filteredLogs.length} entries</Badge>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead className="hidden md:table-cell">Details</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                  <TableCell className="font-mono text-xs">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{log.user}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${getActionColor(log.action)} capitalize`}>
                      {getActionIcon(log.action)}
                      <span className="ml-1">{log.action}</span>
                    </Badge>
                  </TableCell>
                  <TableCell className="capitalize">{log.resource}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-xs truncate">
                    {log.details}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{log.ip}</TableCell>
                  <TableCell>
                    <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>
                      {log.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
